// StrokeAnalyzer — compares a student's recorded strokes against the reference
// vector path using nearest-distance sampling in normalized (0-100) coordinates.

export const TOLERANCE_MODES = {
  easy: 15,
  standard: 10,
  advanced: 7,
};

export const DEFAULT_WEIGHTS = {
  path: 0.5,
  completion: 0.25,
  start: 0.15,
  direction: 0.1,
};

function dist(a, b) {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  return Math.sqrt(dx * dx + dy * dy);
}

// Sample an SVG path string into evenly spaced points in viewBox coordinates.
export function samplePath(pathString, numSamples = 100) {
  const svgns = "http://www.w3.org/2000/svg";
  const svg = document.createElementNS(svgns, "svg");
  const path = document.createElementNS(svgns, "path");
  path.setAttribute("d", pathString);
  svg.appendChild(path);
  svg.style.position = "absolute";
  svg.style.width = "0";
  svg.style.height = "0";
  svg.style.overflow = "hidden";
  document.body.appendChild(svg);
  const total = path.getTotalLength();
  const pts = [];
  for (let i = 0; i <= numSamples; i++) {
    const p = path.getPointAtLength((i / numSamples) * total);
    pts.push({ x: p.x, y: p.y });
  }
  document.body.removeChild(svg);
  return pts;
}

// Reference points for a letter (per-stroke sampling avoids gap artifacts).
export function referencePoints(letter, perStroke = 90) {
  const ref = [];
  letter.strokes.forEach((s) => {
    ref.push(...samplePath(s.d, perStroke));
  });
  return ref;
}

function zeroResult() {
  return {
    score: 0,
    pathAccuracy: 0,
    completion: 0,
    startingPosition: 0,
    direction: 0,
  };
}

/**
 * @param studentStrokes array of { points: [{x,y}, ...] } in 0-100 coords
 * @param letter letter data object
 * @param opts { tolerance, startRadius, weights }
 */
export function analyzeStrokes(studentStrokes, letter, opts = {}) {
  const tolerance = opts.tolerance ?? TOLERANCE_MODES.standard;
  const startRadius = opts.startRadius ?? 16;
  const weights = opts.weights ?? DEFAULT_WEIGHTS;

  const ref = referencePoints(letter);
  const student = [];
  (studentStrokes || []).forEach((st) => {
    if (st && st.points) student.push(...st.points);
  });

  if (student.length < 3 || ref.length === 0) return zeroResult();

  // Path accuracy: fraction of student points near the reference path.
  let within = 0;
  for (const p of student) {
    let min = Infinity;
    for (const r of ref) {
      const d = dist(p, r);
      if (d < min) min = d;
    }
    if (min <= tolerance) within++;
  }
  const pathAccuracy = (within / student.length) * 100;

  // Completion: fraction of reference path covered by the student.
  let covered = 0;
  for (const r of ref) {
    for (const p of student) {
      if (dist(p, r) <= tolerance) {
        covered++;
        break;
      }
    }
  }
  const completion = (covered / ref.length) * 100;

  // Starting position: distance from first student point to the ideal start.
  const dStart = dist(student[0], letter.startingPoint);
  let startScore;
  if (dStart <= startRadius) startScore = 100;
  else startScore = Math.max(0, 100 - ((dStart - startRadius) / startRadius) * 60);

  // Direction: does the student progress along the reference in order?
  const nearestIdx = student.map((p) => {
    let min = Infinity;
    let idx = 0;
    for (let i = 0; i < ref.length; i++) {
      const d = dist(p, ref[i]);
      if (d < min) {
        min = d;
        idx = i;
      }
    }
    return idx;
  });
  let inOrder = 0;
  for (let i = 1; i < nearestIdx.length; i++) {
    if (nearestIdx[i] >= nearestIdx[i - 1] - 2) inOrder++;
  }
  const direction = (inOrder / Math.max(1, nearestIdx.length - 1)) * 100;

  const total =
    pathAccuracy * weights.path +
    completion * weights.completion +
    startScore * weights.start +
    direction * weights.direction;

  return {
    score: Math.round(Math.max(0, Math.min(100, total))),
    pathAccuracy: Math.round(pathAccuracy),
    completion: Math.round(completion),
    startingPosition: Math.round(startScore),
    direction: Math.round(direction),
  };
}

export function feedbackForScore(score) {
  if (score >= 90)
    return { title: "Excellent Work!", stars: 3, tone: "excellent" };
  if (score >= 80) return { title: "Great Job!", stars: 2, tone: "great" };
  if (score >= 70)
    return { title: "Almost There! Try it one more time.", stars: 1, tone: "almost" };
  return { title: "Good Try! Let's practice that letter again.", stars: 0, tone: "try" };
}
